/**
 * UuidGenerator - Service
 * Generates RFC 4122 UUIDs (v1, v3, v4, v5)
 * 
 * Single Responsibility: Generate UUIDs in various formats
 */

type UuidVersion = 1 | 3 | 4 | 5;

interface UuidOptions {
  version?: UuidVersion;
  namespace?: string;
  name?: string;
  uppercase?: boolean;
  removeDashes?: boolean;
}

interface UuidBatchResult {
  uuids: string[];
  count: number;
  version: UuidVersion;
}

class UuidGenerator {
  // Predefined namespaces for v3/v5
  public static readonly NAMESPACE_DNS = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';
  public static readonly NAMESPACE_URL = '6ba7b811-9dad-11d1-80b4-00c04fd430c8';
  public static readonly NAMESPACE_OID = '6ba7b812-9dad-11d1-80b4-00c04fd430c8';
  public static readonly NAMESPACE_X500 = '6ba7b814-9dad-11d1-80b4-00c04fd430c8';

  private static readonly VALID_UUID_REGEX = 
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  /**
   * Generate a single UUID
   */
  generate(options: UuidOptions = {}): string {
    const version = options.version ?? 4;
    let uuid: string;

    switch (version) {
      case 1:
        uuid = this.generateV1();
        break;
      case 3:
        if (!options.namespace || !options.name) {
          throw new Error('UUID v3 requires namespace and name');
        }
        uuid = this.generateV3(options.namespace, options.name);
        break;
      case 4:
        uuid = this.generateV4();
        break;
      case 5:
        if (!options.namespace || !options.name) {
          throw new Error('UUID v5 requires namespace and name');
        }
        uuid = this.generateV5(options.namespace, options.name);
        break;
      default:
        throw new Error(`Unsupported UUID version: ${version}`);
    }

    return this.formatUuid(uuid, options);
  }

  /**
   * Generate multiple UUIDs
   */
  generateBatch(count: number, options: UuidOptions = {}): UuidBatchResult {
    if (count < 1 || count > 1000) {
      throw new Error('Count must be between 1 and 1000');
    }

    const version = options.version ?? 4;
    const uuids: string[] = [];
    
    // For v3/v5, only generate once if namespace and name are the same
    if ((version === 3 || version === 5) && options.namespace && options.name) {
      const uuid = this.generate(options);
      for (let i = 0; i < count; i++) {
        uuids.push(uuid);
      }
    } else {
      for (let i = 0; i < count; i++) {
        uuids.push(this.generate({ ...options, version }));
      }
    }

    return { uuids, count, version };
  }

  /**
   * Validate UUID format
   */
  validate(uuid: string): boolean {
    return UuidGenerator.VALID_UUID_REGEX.test(uuid);
  }

  /**
   * Parse UUID to get version and variant
   */
  parse(uuid: string): { version: number; variant: string } | null {
    if (!this.validate(uuid)) {
      return null;
    }

    const parts = uuid.toLowerCase().split('-');
    const versionPart = parts[2];
    const variantPart = parts[3];
    if (!versionPart || !variantPart) {
      return null;
    }
    const version = parseInt(versionPart[0]!, 16);
    const variantByte = parseInt(variantPart[0]!, 16);
    
    let variant: string;
    if ((variantByte & 0x8) === 0x0) {
      variant = 'NCS';
    } else if ((variantByte & 0xc) === 0x8) {
      variant = 'RFC4122';
    } else if ((variantByte & 0xe) === 0xc) {
      variant = 'Microsoft';
    } else {
      variant = 'Reserved';
    }

    return { version, variant };
  }

  /**
   * Format UUID according to options
   */
  private formatUuid(uuid: string, options: UuidOptions): string {
    let result = uuid;

    if (options.removeDashes) {
      result = result.replace(/-/g, '');
    }

    if (options.uppercase) {
      result = result.toUpperCase();
    }

    return result;
  }

  /**
   * Generate UUID v1 (timestamp-based)
   * Note: In browser environment, we use random node to avoid tracking
   */
  private generateV1(): string {
    const now = Date.now();
    const timestamp = now + 12219292800000; // Add UUID epoch offset
    
    const timeLow = timestamp & 0xffffffff;
    const timeMid = (timestamp >> 32) & 0xffff;
    const timeHighAndVersion = ((timestamp >> 48) & 0x0fff) | 0x1000;
    
    const clockSeq = (Math.random() * 0x3fff) | 0x8000;
    const node = Array.from({ length: 6 }, () => Math.random() * 256 | 0);
    
    return [
      timeLow.toString(16).padStart(8, '0'),
      timeMid.toString(16).padStart(4, '0'),
      timeHighAndVersion.toString(16).padStart(4, '0'),
      clockSeq.toString(16).padStart(4, '0'),
      node.map(b => b.toString(16).padStart(2, '0')).join('')
    ].join('-');
  }

  /**
   * Generate UUID v3 (namespace + name, MD5-based)
   */
  private generateV3(namespace: string, name: string): string {
    // In browser, we use a simplified v3 implementation
    // For production, consider using crypto.subtle
    const combined = namespace + name;
    const hash = this.simpleHash(combined);
    
    return this.hashToUuid(hash, 0x3000);
  }

  /**
   * Generate UUID v4 (random)
   */
  private generateV4(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    
    // Set version (4) and variant (10)
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    
    return this.bytesToUuid(bytes);
  }

  /**
   * Generate UUID v5 (namespace + name, SHA-1-based)
   */
  private generateV5(namespace: string, name: string): string {
    // In browser, we use a simplified v5 implementation
    // For production, consider using crypto.subtle
    const combined = namespace + name;
    const hash = this.simpleHash(combined);
    
    return this.hashToUuid(hash, 0x5000);
  }

  /**
   * Simple hash function for v3/v5 (for demo purposes)
   * In production, use proper MD5/SHA1
   */
  private simpleHash(str: string): Uint8Array {
    const hash = new Uint8Array(16);
    let h1 = 0x67452301;
    let h2 = 0xefcdab89;
    let h3 = 0x98badcfe;
    let h4 = 0x10325476;
    
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      h1 = ((h1 << 5) - h1 + char) | 0;
      h2 = ((h2 << 5) - h2 + char * 2) | 0;
      h3 = ((h3 << 5) - h3 + char * 3) | 0;
      h4 = ((h4 << 5) - h4 + char * 4) | 0;
    }
    
    const view = new DataView(hash.buffer);
    view.setUint32(0, h1!, false);
    view.setUint32(4, h2!, false);
    view.setUint32(8, h3!, false);
    view.setUint32(12, h4!, false);
    
    return hash;
  }

  /**
   * Convert hash bytes to UUID string with version
   */
  private hashToUuid(hash: Uint8Array, version: number): string {
    const bytes = new Uint8Array(hash);
    // Set version
    bytes[6] = (bytes[6]! & 0x0f) | ((version >> 12) & 0xf0);
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    
    return this.bytesToUuid(bytes);
  }

  /**
   * Convert bytes to UUID string
   */
  private bytesToUuid(bytes: Uint8Array): string {
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0'));
    return [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10, 16).join('')
    ].join('-') as string;
  }
}

export { UuidGenerator };
export type { UuidVersion, UuidOptions, UuidBatchResult };
